import React, { useCallback, useEffect, useRef, useState } from 'react';
import './FeedbackModal.css';

export type FeedbackType = 'bug' | 'idea' | 'help';

const BugIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2l1.88 1.88" />
    <path d="M14.12 3.88L16 2" />
    <path d="M9 7.13v-1a3.003 3.003 0 116 0v1" />
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 014-4h4a4 4 0 014 4v3c0 3.3-2.7 6-6 6" />
    <path d="M12 20v-9" />
    <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
    <path d="M6 13H2" />
    <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
    <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
    <path d="M22 13h-4" />
    <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
  </svg>
);

const LightbulbIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 006 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
    <path d="M9 18h6" />
    <path d="M10 22h4" />
  </svg>
);

const HelpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const CheckCircleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
    <path d="M22 4L12 14.01l-3-3" />
  </svg>
);

const SpinnerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v4" />
    <path d="M12 18v4" />
    <path d="M4.93 4.93l2.83 2.83" />
    <path d="M16.24 16.24l2.83 2.83" />
    <path d="M2 12h4" />
    <path d="M18 12h4" />
    <path d="M4.93 19.07l2.83-2.83" />
    <path d="M16.24 7.76l2.83-2.83" />
  </svg>
);

type FeedbackTypeOption = {
  id: FeedbackType;
  label: string;
  icon: React.ReactNode;
  subscript?: string;
};

const feedbackTypeOptions: FeedbackTypeOption[] = [
  {
    id: 'idea',
    label: 'Share an Idea',
    icon: <LightbulbIcon className="feedback-type-icon" />,
    subscript: '(or Feedback)',
  },
  {
    id: 'bug',
    label: 'Report a Bug',
    icon: <BugIcon className="feedback-type-icon" />,
  },
  {
    id: 'help',
    label: 'Need Help',
    icon: <HelpIcon className="feedback-type-icon" />,
  },
];

export interface FeedbackModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  isSuccess: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (type: FeedbackType, message: string) => void;
}

function sanitizeClientText(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';
  let out = '';
  for (let i = 0; i < trimmed.length; i += 1) {
    const code = trimmed.charCodeAt(i);
    const isControl = (code >= 0 && code <= 31) || code === 127;
    if (isControl) continue;
    out += trimmed[i];
  }
  return out.trim();
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  isSubmitting,
  isSuccess,
  error,
  onClose,
  onSubmit,
}) => {
  const [selectedType, setSelectedType] = useState<FeedbackType>('bug');
  const [message, setMessage] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    window.setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      setSelectedType('bug');
      setMessage('');
      setIsClosing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && textareaRef.current && !isSuccess) {
      window.setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen, isSuccess]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose, isOpen, isSubmitting]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      handleClose();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const cleaned = sanitizeClientText(message);
    if (!cleaned) return;
    onSubmit(selectedType, cleaned);
  };

  if (!isOpen) return null;

  return (
    <div
      className={`feedback-backdrop ${isClosing ? 'closing' : ''}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
    >
      <div className={`feedback-modal ${isClosing ? 'closing' : ''}`}>
        <div className="feedback-header">
          <h2 id="feedback-title" className="feedback-title">
            {isSuccess ? 'Thank You!' : 'Send Feedback'}
          </h2>
          <button
            type="button"
            className="feedback-close-btn"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close"
          >
            <CloseIcon className="feedback-close-icon" />
          </button>
        </div>

        {isSuccess ? (
          <div className="feedback-success">
            <div className="feedback-success-icon-wrapper">
              <CheckCircleIcon className="feedback-success-icon" />
            </div>
            <p className="feedback-success-text">Your feedback has been sent successfully.</p>
            <p className="feedback-success-subtext">We appreciate you taking the time to help us improve!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="feedback-form">
            <div className="feedback-section">
              <label className="feedback-label">What would you like to do?</label>
              <div className="feedback-type-grid">
                {feedbackTypeOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`feedback-type-btn ${selectedType === option.id ? 'selected' : ''}`}
                    onClick={() => setSelectedType(option.id)}
                    disabled={isSubmitting}
                  >
                    <span className="feedback-type-icon-wrapper">{option.icon}</span>
                    <span className="feedback-type-label">
                      {option.label}
                      {option.subscript ? <span className="feedback-type-subscript">{option.subscript}</span> : null}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="feedback-section">
              <label htmlFor="feedback-message" className="feedback-label">
                {selectedType === 'bug' && 'Describe the issue'}
                {selectedType === 'idea' && 'Tell us your idea'}
                {selectedType === 'help' && 'How can we help?'}
                <span className="feedback-required">*</span>
              </label>
              <textarea
                ref={textareaRef}
                id="feedback-message"
                className="feedback-textarea"
                placeholder={
                  selectedType === 'bug'
                    ? 'Please describe what happened, what you expected, and steps to reproduce...'
                    : selectedType === 'idea'
                    ? 'Share your suggestion or feature request...'
                    : 'Tell us what you need help with...'
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSubmitting}
                rows={4}
                maxLength={2000}
              />
              <div className="feedback-char-count">{message.length} / 2000</div>
            </div>

            {error ? (
              <div className="feedback-error" role="alert">
                <svg className="feedback-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4" />
                  <path d="M12 16h.01" />
                </svg>
                <span>{error}</span>
              </div>
            ) : null}

            <div className="feedback-footer">
              <button type="button" className="feedback-btn feedback-btn-secondary" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button type="submit" className="feedback-btn feedback-btn-primary" disabled={!sanitizeClientText(message) || isSubmitting}>
                {isSubmitting ? (
                  <>
                    <SpinnerIcon className="feedback-spinner" />
                    <span>Sending...</span>
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
